/**
 * V1 (Fulke 1563) piece values and layout constants.
 *
 * V1 uses asymmetric piece values: White (Evens) and Black (Odds) have different value sets,
 * derived per Fulke's mathematical pipeline (n, n*n, n*(n+1), (n+1)*(n+1), (2n+1)*(n+1),
 * (2n+1)*(2n+1)) applied to the four base numbers of each side. See Rithmomachia-Rules-Fulke.md
 * for the historical derivation.
 *
 * White Rounds:    2, 4, 6, 8 (row 1) + 4, 16, 36, 64 (row 2)   →  8 total
 * White Triangles: 6, 20, 42, 72 + 9, 25, 49, 81                →  8 total
 * White Squares:   15, 45, 153 + 25, 81, 169, 289               →  7 total
 *                  (the King is the eighth Square, value 91)
 *
 * Black Rounds:    3, 5, 7, 9 + 9, 25, 49, 81                   →  8 total
 * Black Triangles: 12, 30, 56, 90 + 16, 36, 64, 100             →  8 total
 * Black Squares:   28, 66, 120 + 49, 121, 225, 361              →  7 total
 *                  (the King is the eighth Square, value 190)
 *
 * Each side: 8 R + 8 T + 7 S + 1 King = 24 pieces.
 */

export const WHITE_ROUND_VALUES = [2, 4, 6, 8, 4, 16, 36, 64] as const;
export const BLACK_ROUND_VALUES = [3, 5, 7, 9, 9, 25, 49, 81] as const;

export const WHITE_TRIANGLE_VALUES = [6, 20, 42, 72, 9, 25, 49, 81] as const;
export const BLACK_TRIANGLE_VALUES = [12, 30, 56, 90, 16, 36, 64, 100] as const;

export const WHITE_SQUARE_VALUES = [15, 45, 153, 25, 81, 169, 289] as const;
export const BLACK_SQUARE_VALUES = [28, 66, 120, 49, 121, 225, 361] as const;

export const WHITE_PYRAMID_VALUE = 91;
export const BLACK_PYRAMID_VALUE = 190;

/**
 * Pyramid layer composition, top → bottom (Fulke "Of the kings", lines ~344–351, 1244–1248).
 *
 * White King has 6 layers totaling 91, with a pointed cap (the Round-1).
 * Black King has 5 layers totaling 190, no point (smallest layer is Round-16, not 1).
 *
 * Each layer carries a numeric "quadrate" value used for capture math and a movement shape
 * used to determine what movement options the king still has after partial capture.
 */
export interface PyramidLayerSpec {
  shape: "round" | "triangle" | "square";
  value: number;
}

export const WHITE_PYRAMID_LAYERS: readonly PyramidLayerSpec[] = [
  { shape: "round", value: 1 },
  { shape: "round", value: 4 },
  { shape: "triangle", value: 9 },
  { shape: "triangle", value: 16 },
  { shape: "square", value: 25 },
  { shape: "square", value: 36 },
] as const;

export const BLACK_PYRAMID_LAYERS: readonly PyramidLayerSpec[] = [
  { shape: "round", value: 16 },
  { shape: "triangle", value: 25 },
  { shape: "triangle", value: 36 },
  { shape: "square", value: 49 },
  { shape: "square", value: 64 },
] as const;

/**
 * Movement step distances per Fulke V1 ("first kynde"), with one house-rule adjustment for
 * the Round.
 *
 *   Round:    1 step in ANY direction (8-way king's-step). HOUSE RULE — Fulke's text says
 *             diagonal only, but the diagonal-only Round is color-locked, which removes half
 *             the board from any Round's reach.
 *   Triangle: 2 orthogonal steps, leaping over the intermediate square.
 *   Square:   3 orthogonal steps, leaping over the two intermediate squares.
 *
 * Triangles and Squares also have non-capturing "flying" knight-style moves — Triangle (1,2)
 * and Square (1,3). Flying is non-capturing for replacement captures but is usable for
 * setting up custodian (Siege) captures.
 */
export const ROUND_STEP = 1;
export const TRIANGLE_STEP = 2;
export const SQUARE_STEP = 3;

/**
 * Greater Victory enemy area. Fulke says "the first five ranks" (with some players using six).
 * We use 5 — that's the primary Fulke number.
 *
 * Coordinates: White's home is at the TOP of the board in our V1 layout (ranks 0–5), Black's
 * home is at the BOTTOM (ranks 10–15). The Greater Victory area for each side is the first
 * five ranks of the opponent's side — White triumphs in ranks 11–15 (Black's home + back ranks),
 * Black triumphs in ranks 0–4.
 */
export const GREATER_VICTORY_AREA_DEPTH = 5;

/** Half-board boundary, returned in case the rest of the engine still uses an enemy-territory check. */
export function isInEnemyTerritory(side: "white" | "black", rank: number): boolean {
  // V1 layout: White at top (ranks 0–5), Black at bottom (ranks 10–15).
  // The enemy half (for territory progression) is the opposite half of the board.
  return side === "white" ? rank >= 8 : rank <= 7;
}

/**
 * "First five ranks of the opponent's side" — the strict V1 Greater Victory area. White triumph
 * in Black's first five ranks; Black triumph in White's first five ranks.
 */
export function isInGreaterVictoryArea(side: "white" | "black", rank: number): boolean {
  if (side === "white") {
    // White's triumph area is ranks (16 - 5) through 15 = 11..15.
    return rank >= 16 - GREATER_VICTORY_AREA_DEPTH;
  }
  // Black's triumph area is ranks 0..4.
  return rank < GREATER_VICTORY_AREA_DEPTH;
}
